import { Box, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { useFormFactor } from '../../../hooks'
import { THUMBNAIL_ASPECT_RATIO } from '../../../helpers/content-image'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

const multiNoWrap = lines => {
  return {
    lineClamp: lines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical'
  }
}

// PENG-3630: the image is locked to 16:9, the ratio editors upload and crop
// at in the Ark, so copy baked into the image is no longer cropped away.
// From md up this is the full-width hero: the image sits 16px in from the
// card's edges with rounded corners, like the vertical cards below it, and
// sets the card's height; the text sits centred beside it. Below md the
// image stacks above the content, which no longer overlaps it.
const stackedContentContainer = {
  width: 'auto'
}

const contentContainer = {
  flex: 1,
  // Override the calc()-based width that lives in the shared
  // PromotedContentCardContent styles — flex sizing handles the panel now.
  width: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
}

const useStyles = makeStyles(({ spacing, palette, typography, radius }) => ({
  root: {
    height: '100%'
  },
  container: props => props.isStacked
    ? {
        width: '100%'
      }
    : {
        width: '100%',
        alignItems: 'center'
      },
  imgContainer: props => props.isStacked
    ? { padding: spacing(2, 2, 0, 2) }
    : {
        flex: '0 0 55%',
        boxSizing: 'border-box',
        padding: spacing(2, 0, 2, 2)
      },
  // The content Box tints on hover. The 8px margin keeps that tint off the
  // card's edges and the image, like SecondaryCard's inset; padding shrinks by
  // the same amount so the text stays where it was. `&&` beats the Box's own
  // `p` / `m` props and the shared calc() width, whatever order the sheets
  // land in.
  contentContainer: props => props.isStacked
    ? {
        ...stackedContentContainer,
        background: palette.background.paper,
        '&&': { width: 'auto', margin: spacing(1), padding: spacing(1) }
      }
    : {
        ...contentContainer,
        background: palette.background.paper,
        '&&': { width: 'auto', margin: spacing(1), padding: spacing(2, 3) }
      },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    aspectRatio: THUMBNAIL_ASPECT_RATIO,
    objectFit: 'cover',
    borderRadius: radius.lg
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '28px',
    lineHeight: '32px',
    letterSpacing: '0.6px',
    color: '#000',
    ...multiNoWrap(2)
  },
  cardSummary: {
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '24px',
    color: '#454545',
    ...multiNoWrap(4)
  },
  time: {
    color: palette.primary.main,
    fontWeight: typography.fontWeightBold
  },
  actions: {
    marginTop: spacing(1)
  }
}))

const PromotedContentMainCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, ...props }) => {
  const { isMobile, isSmallTablet } = useFormFactor()
  const isStacked = isMobile || isSmallTablet
  const classes = useStyles({ isStacked })

  const {
    contentImage,
    handleCardSelect,
    expanded,
    cardSize,
    hoverShadow,
    actions
  } = usePromotedCardMethods({ content, position, size, cardType: PROMOTED_CARD_TYPES.MAIN, onSelect, onAddToPlaylist })

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardSelect()
    }
  }

  return (
    <PromotedCardContainer
      expand={expanded}
      size={cardSize}
      hoverShadow={hoverShadow}
      content={content}
      testId={testId}
    >
      <Box display='flex' flexDirection={isStacked ? 'column' : 'row'} className={classes.container}>
        <Box
          className={classes.imgContainer}
          onClick={handleCardSelect}
          onKeyDown={handleImageKeyDown}
          role='button'
          tabIndex={0}
          aria-label={content ? `View ${content.title}` : 'Loading content'}
          sx={{
            cursor: 'pointer',
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px'
            }
          }}
        >
          {
              content
                ? (
                  <img
                    src={contentImage}
                    title={content?.image?.altText}
                    className={classes.image}
                    data-test={`card-image-${content?.friendlyUrl}`}
                    alt={content?.image?.altText || ''}
                  />
                  )
                : (
                  <Skeleton variant='rectangular' className={classes.image} />
                  )
            }
        </Box>
        <PromotedContentCardContent
          content={content}
          cardType={PROMOTED_CARD_TYPES.MAIN}
          actions={actions}
          onSelect={handleCardSelect}
          summaryLines={isStacked ? 2 : 3}
          expand={expanded}
          subtitleMargin={1}
          overrides={{
            contentContainer: classes.contentContainer,
            actions: classes.actions
          }}
        />
      </Box>
    </PromotedCardContainer>
  )
}

export default PromotedContentMainCard
